import {useEffect, useRef, useState} from 'react';
import {CheckCircle2, Loader2, AlertCircle, FileSpreadsheet} from 'lucide-react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {Progress} from '@common-web/components/ui/progress';
import {trpc} from '@common-web/lib/trpc';
import type {ImportResult} from './types';

type ProcessingStepProps = {
  jobRunId: string;
  totalRows: number;
  fileName: string | undefined;
  onComplete: (result: ImportResult) => void;
  onError: (message: string) => void;
};

export function ProcessingStep({
  jobRunId,
  totalRows,
  fileName,
  onComplete,
  onError,
}: ProcessingStepProps) {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const didFinish = useRef(false);
  const completeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [finished, setFinished] = useState(false);

  const statusQuery = trpc.group.vbaMembers.importEligibilityStatus.useQuery(
    {jobRunId},
    {
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        if (status === 'COMPLETED' || status === 'FAILED') return false;
        return 3000;
      },
    },
  );

  const {data} = statusQuery;

  useEffect(() => {
    if (!data || didFinish.current) return;

    // A FAILED run that still produced a result means the import ran to the end
    // with per-row errors — route it to the results screen so the user sees the
    // created/error breakdown instead of a generic failure message.
    if ((data.status === 'COMPLETED' || data.status === 'FAILED') && data.result) {
      didFinish.current = true;
      setFinished(true);
      completeTimerRef.current = setTimeout(() => onCompleteRef.current(data.result!), 1200);
    } else if (data.status === 'FAILED') {
      didFinish.current = true;
      onErrorRef.current(data.errorMessage ?? 'Import job failed unexpectedly');
    }
  }, [data]);

  useEffect(() => {
    return () => {
      if (completeTimerRef.current) clearTimeout(completeTimerRef.current);
    };
  }, []);

  const isFailed = data?.status === 'FAILED' && !data.result;
  const progress = data?.progress;
  const percent = progress ? Math.round((progress.processed / progress.total) * 100) : 0;
  const isRunning = data?.status === 'RUNNING';

  const heading = isFailed
    ? 'Import Failed'
    : finished
      ? 'Import Complete'
      : 'Processing Import';

  return (
    <div className='space-y-6'>
      <Card className='border-0 shadow-lg bg-white overflow-hidden'>
        <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
          <CardTitle className='text-lg font-semibold text-brand-navy'>{heading}</CardTitle>
        </CardHeader>
        <CardContent className='p-6'>
          <div className='flex flex-col items-center justify-center py-12 gap-6'>
            {isFailed ? (
              <>
                <div className='rounded-full bg-red-100 p-4'>
                  <AlertCircle className='h-8 w-8 text-red-600' />
                </div>
                <Alert variant='destructive' className='max-w-md'>
                  <AlertCircle className='h-4 w-4' />
                  <AlertDescription>
                    {data?.errorMessage ?? 'An unexpected error occurred during import.'}
                  </AlertDescription>
                </Alert>
              </>
            ) : finished ? (
              <>
                <div className='rounded-full bg-green-100 p-4'>
                  <CheckCircle2 className='h-8 w-8 text-green-600' />
                </div>
                <p className='text-lg font-semibold text-brand-navy'>
                  Import finished — loading results…
                </p>
              </>
            ) : (
              <>
                <div className='rounded-full bg-brand-blue/10 p-4'>
                  <Loader2 className='h-8 w-8 text-brand-blue animate-spin' />
                </div>
                <div className='text-center space-y-2'>
                  <p className='text-lg font-semibold text-brand-navy'>
                    {isRunning && progress
                      ? `Importing members (${progress.processed} of ${progress.total})…`
                      : 'Waiting for import to begin…'}
                  </p>
                  <p className='text-sm text-gray-500'>
                    This may take a few minutes for large files. You can safely leave this page
                    and check back later.
                  </p>
                </div>

                {isRunning && progress && (
                  <div className='w-full max-w-md space-y-2'>
                    <Progress value={percent} className='h-3' />
                    <p className='text-center text-sm font-medium text-gray-600'>
                      {percent}% complete
                    </p>
                  </div>
                )}

                <div className='flex items-center gap-3 text-sm text-gray-500'>
                  <FileSpreadsheet className='h-4 w-4' />
                  <span>
                    {fileName ?? 'import.csv'} &middot; {totalRows} rows
                  </span>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
